// Profile & settings — reachable from either shell (student sidebar / teacher
// top bar) via the ProfileMenu, and rendered inside that same shell. Lets the user
// edit their display name, see their verification status + curriculum, change
// their password, and log out. The profile is read/written through the app's
// own API (GET/PUT /me/profile). Board and grade are read-only in the Profile
// card and change only under Advanced, through their own endpoint — a scope
// change re-points every scoped query the student makes, so it must not ride
// along with a name edit.
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { decodeClaims, roleLabel } from '@/lib/auth';
import { apiGet, apiPatch, apiPut } from '@/lib/api';
import { requestPasswordReset, signOut } from '@/lib/session';
import { useSession } from '@/hooks/useSession';
import { useCurriculumLevels } from '@/hooks/useCurriculumLevels';
import { gradeLabel } from '@cwy/config/contracts';
import type { Board, Grade, UserProfileDTO } from '@/types';
import { Button, Card, Field, Input, Select, StudentPage, StudentPageHeader } from '@/components/ds';

/**
 * Shape returned by GET /me/profile.
 *
 * The server's own DTO, not a local copy. A hand-written mirror of a server
 * shape is what let the two drift before — and `school`, which used to be a
 * field here, no longer exists at all.
 */
type Account = UserProfileDTO;

const gradeName = (grade: Grade): string => `Grade ${gradeLabel(grade)}`;

/** Human-friendly message from an API error. */
function messageFor(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null) {
    const e = err as { message?: string };
    if (e.message) return e.message;
  }
  return fallback;
}

export function SettingsPage() {
  const [user, setUser] = useState<Account | null>(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let alive = true;
    apiGet<Account>('/me/profile')
      .then((u) => {
        if (alive) setUser(u);
      })
      .catch((err) => {
        if (alive) setLoadError(messageFor(err, 'Could not load your account.'));
      });
    return () => {
      alive = false;
    };
  }, []);

  // The session guard lives on the route (RequireAuth in components/AuthGate),
  // so anyone rendering here is signed in, verified and onboarded.
  const claimsForRole = decodeClaims();
  const membershipRole = claimsForRole?.role;
  const isStaff =
    membershipRole === 'TEACHER' ||
    membershipRole === 'COUNSELLOR' ||
    membershipRole === 'SCHOOL_ADMIN';

  // No page chrome and no back button: this renders inside the caller's own
  // shell (see components/RoleShell), so the sidebar is the way out.
  return (
    <StudentPage>
      <StudentPageHeader
        title="Profile & settings"
        subtitle="Your account, sign-in and curriculum."
      />

      <div className="flex max-w-[640px] flex-col gap-5">
        {loadError && <p className="text-sm text-danger">{loadError}</p>}
        {!user && !loadError && <p className="text-ink-soft">Loading your account…</p>}

        {user && (
          <>
            <ProfileCard user={user} role={roleLabel(claimsForRole)} onUpdated={setUser} />
            <PasswordCard />
            {!isStaff && user.board && user.grade && (
              <AdvancedCard user={user} onUpdated={(u) => setUser({ ...user, ...u })} />
            )}
            <Card title="Sign out">
              <div className="flex items-center justify-between gap-4">
                <div className="text-sm text-ink-soft">End your session on this device.</div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    // Ends the session server-side, then redirects — so the next
                    // visitor on a shared machine is not signed straight back in
                    // without a password.
                    void signOut();
                  }}
                >
                  Log out
                </Button>
              </div>
            </Card>
          </>
        )}
      </div>
    </StudentPage>
  );
}

// ─────────────────────────── Profile card ───────────────────────────
function ProfileCard({
  user,
  role,
  onUpdated,
}: {
  user: Account;
  role: string;
  onUpdated: (u: Account) => void;
}) {
  // Verification state lives on the session claim, set server-side by the
  // email-verification flow — not on the profile row.
  const emailVerified = decodeClaims()?.emailVerified === true;
  const [firstName, setFirstName] = useState(user.firstName ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const curriculum =
    user.board && user.grade ? `${gradeName(user.grade)} · ${user.board}` : 'Not set';
  const dirty =
    firstName.trim() !== (user.firstName ?? '') || lastName.trim() !== (user.lastName ?? '');

  const save = async () => {
    setSaving(true);
    setMsg('');
    setErr('');
    try {
      const updated = await apiPut<Account>('/me/profile', {
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
      });
      onUpdated({ ...user, ...updated });
      setMsg('Profile updated.');
    } catch (e) {
      setErr(messageFor(e, 'Could not update your profile.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card title="Profile">
      <div className="flex items-center gap-4">
        <div
          data-numeric=""
          className="grid h-14 w-14 flex-none place-items-center rounded-full text-xl font-bold"
          style={{ background: 'var(--brand-lime)', color: 'var(--brand-forest)' }}
        >
          {(user.email?.[0] ?? 'U').toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="truncate text-base font-bold text-ink">{user.email}</span>
            {emailVerified ? (
              <Badge tone="green">Verified</Badge>
            ) : (
              <Badge tone="amber">Unverified</Badge>
            )}
          </div>
          <div className="text-xs text-ink-soft">
            {role} · {curriculum}
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="flex flex-wrap items-end gap-2">
          <Field label="First name" className="min-w-[150px] flex-1">
            {({ id }) => (
              <Input
                id={id}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                maxLength={60}
              />
            )}
          </Field>
          <Field label="Last name" className="min-w-[150px] flex-1">
            {({ id }) => (
              <Input
                id={id}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                maxLength={60}
              />
            )}
          </Field>
          <Button disabled={!dirty || saving} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
        {msg && <p className="mt-2 text-sm text-forest">{msg}</p>}
        {err && <p className="mt-2 text-sm text-danger">{err}</p>}
      </div>
    </Card>
  );
}

// ─────────────────────────── Password card ───────────────────────────
//
// An emailed reset link rather than an in-page form. The reasoning that pointed
// the old version at a hosted account console still applies: proving possession
// of the mailbox is a stronger check than a form field, and it keeps the new
// credential out of the app's own UI. What changed is that the flow is now ours
// — there is no external console to open in a new tab.
function PasswordCard() {
  const { data: session } = useSession();
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');

  const handlePasswordReset = async () => {
    const email = session?.user.email;
    if (!email) return;
    setResetError('');
    try {
      await requestPasswordReset(email);
      setResetSent(true);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Could not send the link.');
    }
  };

  return (
    <Card title="Password & security" hint="Managed by your CWY account">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-ink">Change your password</div>
          <div className="text-xs text-ink-soft">
            {resetError ||
              (resetSent ? 'Check your inbox.' : "We'll email you a link to set a new one.")}
          </div>
        </div>
        <Button variant="secondary" onClick={() => void handlePasswordReset()}>
          {resetSent ? 'Link sent' : 'Send link'}
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────────── Advanced (academic scope) ───────────────────────
//
// Collapsed by default and deliberately plain. Changing board or grade is a rare
// action with wide consequences — a student who has come here to fix a name
// should not be able to trip over it.
//
// Nothing is deleted by the change: past work stays in the account, attributed
// to the cohort it was earned in, and simply stops matching the scoped queries
// behind the dashboards. The dialog says exactly that, with this student's own
// numbers rather than a generic warning.

/** GET /me/academic-scope. */
interface ScopeInfo {
  board: Board;
  grade: Grade;
  periods: { board: Board; grade: Grade; reason: string; startedAt: string; endedAt: string | null }[];
  current: { submittedPapers: number; practiceAnswers: number };
}

function scopeLabel(board: Board, grade: Grade): string {
  return `${gradeName(grade)} · ${board}`;
}

function AdvancedCard({ user, onUpdated }: { user: Account; onUpdated: (u: Partial<Account>) => void }) {
  const [expanded, setExpanded] = useState(false);
  const [board, setBoard] = useState<Board>(user.board!);
  const [grade, setGrade] = useState<Grade>(user.grade!);
  const [info, setInfo] = useState<ScopeInfo | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  // Only a board and grade CWY has a syllabus for — never one that exists with
  // nothing under it yet.
  const levels = useCurriculumLevels(expanded);
  const cohorts = levels.data?.cohorts ?? [{ board: user.board!, grade: user.grade! }];
  const boardChoices = [...new Set(cohorts.map((c) => c.board))];
  const gradeChoices = cohorts.filter((c) => c.board === board).map((c) => c.grade);

  // Loaded on expand rather than on mount: most visits to this page never open
  // the section, and the counts are only needed to word the dialog.
  useEffect(() => {
    if (!expanded || info) return;
    let alive = true;
    apiGet<ScopeInfo>('/me/academic-scope')
      .then((s) => alive && setInfo(s))
      .catch(() => {
        /* The dialog falls back to its generic wording. */
      });
    return () => {
      alive = false;
    };
  }, [expanded, info]);

  const changed = board !== user.board || grade !== user.grade;

  const confirm = async () => {
    setSaving(true);
    setErr('');
    try {
      const saved = await apiPatch<{ board: Board; grade: Grade }>('/me/academic-scope', {
        board,
        grade,
      });
      onUpdated({ board: saved.board, grade: saved.grade });
      setInfo(null);
      setConfirming(false);
      setMsg(`You're now in ${scopeLabel(saved.board, saved.grade)}.`);
    } catch (e) {
      setErr(messageFor(e, 'Could not change your board or grade.'));
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  const hasWork = (info?.current.submittedPapers ?? 0) + (info?.current.practiceAnswers ?? 0) > 0;

  return (
    <Card>
      <button
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="advanced-panel"
        className="flex w-full items-center justify-between gap-4 text-left"
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
      >
        <span>
          <span
            className="block"
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-h3)',
              fontWeight: 600,
              color: 'var(--text-accent)',
            }}
          >
            Advanced
          </span>
          <span className="mt-1 block text-xs text-ink-soft">Change your board or grade</span>
        </span>
        <span
          aria-hidden
          className="text-ink-soft"
          style={{
            transform: expanded ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-base) var(--ease-standard)',
          }}
        >
          ▾
        </span>
      </button>

      {expanded && (
        <div id="advanced-panel" className="mt-5 border-t border-line pt-5">
          <p className="text-xs text-ink-soft">
            Your board and grade decide which syllabus, papers and practice you see. Changing them
            keeps everything you've already done — it just stops showing work from the syllabus
            you've left.
          </p>

          <div className="mt-4 flex flex-wrap items-end gap-2">
            <Field label="Board" className="min-w-[150px] flex-1">
              {({ id }) => (
                <Select
                  id={id}
                  value={board}
                  onChange={(e) => {
                    const next = e.target.value as Board;
                    setBoard(next);
                    // Keep the grade if the new board teaches it; else its first.
                    const grades = cohorts.filter((c) => c.board === next).map((c) => c.grade);
                    if (!grades.includes(grade) && grades[0]) setGrade(grades[0]);
                  }}
                >
                  {boardChoices.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Grade" className="min-w-[150px] flex-1">
              {({ id }) => (
                <Select id={id} value={grade} onChange={(e) => setGrade(e.target.value as Grade)}>
                  {gradeChoices.map((g) => (
                    <option key={g} value={g}>
                      {gradeName(g)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Button disabled={!changed || saving} onClick={() => setConfirming(true)}>
              Save
            </Button>
          </div>

          {msg && <p className="mt-2 text-sm text-forest">{msg}</p>}
          {err && <p className="mt-2 text-sm text-danger">{err}</p>}
        </div>
      )}

      <AnimatePresence>
        {confirming && (
          <motion.div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => !saving && setConfirming(false)}
          >
            <motion.div
              className="w-full max-w-md rounded-t-2xl bg-card p-5 shadow-xl sm:rounded-2xl"
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label="Confirm board and grade change"
            >
              <h3 className="text-lg font-bold text-forest">Change your board and grade?</h3>
              <p className="mt-2 text-sm text-ink-soft">
                You're moving from <strong>{scopeLabel(user.board!, user.grade!)}</strong> to{' '}
                <strong>{scopeLabel(board, grade)}</strong>.
              </p>

              <ul className="mt-3 flex flex-col gap-2 text-sm text-ink-soft">
                {hasWork ? (
                  <li>
                    Your {scopeLabel(user.board!, user.grade!)} work — {info!.current.submittedPapers}{' '}
                    paper{info!.current.submittedPapers === 1 ? '' : 's'} and{' '}
                    {info!.current.practiceAnswers} practice answer
                    {info!.current.practiceAnswers === 1 ? '' : 's'} — stays saved to your account,
                    but stops appearing in your dashboards, reports and review queue.
                  </li>
                ) : (
                  <li>
                    You haven't completed any papers or practice yet, so there's nothing to carry
                    over.
                  </li>
                )}
                <li>Your streak, progress and account details are not affected.</li>
                <li>Today's study plan and this week's sheet are rebuilt for the new syllabus.</li>
                <li>Your mastery cards from the old syllabus stop coming up for review.</li>
              </ul>

              <div className="mt-5 flex justify-end gap-2">
                <Button variant="secondary" disabled={saving} onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
                <Button disabled={saving} onClick={() => void confirm()}>
                  {saving ? 'Changing…' : 'Yes, change it'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

function Badge({ children, tone }: { children: React.ReactNode; tone: 'green' | 'amber' }) {
  const styles =
    tone === 'green'
      ? { background: 'var(--green-bg, #dcfce7)', color: 'var(--green-text, #166534)' }
      : { background: '#fef3c7', color: '#92400e' };
  return (
    <span className="flex-none rounded-full px-2 py-0.5 text-fs-11 font-semibold" style={styles}>
      {children}
    </span>
  );
}
